import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, InputSignal, effect, input, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { TranslatePipe } from '@ngx-translate/core';
import type { ChartData, ChartOptions } from 'chart.js';
import { CardModule } from 'primeng/card';
import { DatePicker } from 'primeng/datepicker';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import { Subject } from 'rxjs';
import { PhxChartComponent } from '../../../../../shared/components/phx-chart/phx-chart.component';
import { PhxCheckboxComponent } from '../../../../../shared/components/phx-input-checkbox/phx-input-checkbox.component';
import { I18nService } from '../../../../../@core/services/i18n/i18n.service';
import { Product, ProductDispositionTimeSeriesEntry, ProductMinAndTargetStock } from '../../../../../generated/generated-types';
import { ProductDispositionService } from '../../../services/product-disposiiton.service';
import { ProductService } from '../../../services/product.service';

@Component({
  selector: 'app-product-stock-line-chart',
  templateUrl: './product-stock-line-chart.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    TranslatePipe,
    DatePicker,
    PhxChartComponent,
    CardModule,
    ProgressSpinnerModule,
    TooltipModule,
    PhxCheckboxComponent,
    FormsModule,
  ]
})
export class ProductStockLineChartComponent {

  productIdInputSig: InputSignal<string> = input<string>();

  @Input()
  product: Product;

  destroy$ = new Subject<void>();

  data: ChartData<'line'>;
  options: ChartOptions<'line'>;

  fromDate: Date | undefined;
  endDate: Date | undefined;

  private lastLoadKey = '';

  isLoadingSig = signal<boolean>(true);

  productMinAndTargetStock: ProductMinAndTargetStock = null;

  productDispositionTimeSeriesEntries: ProductDispositionTimeSeriesEntry[] = [];

  showEstimatedInboundQuantity = false;

  documentStyle = getComputedStyle(document.documentElement);
  textColor = this.documentStyle.getPropertyValue('--p-primary-500');

  textColorSecondary = this.documentStyle.getPropertyValue('--p-slate-400');
  surfaceBorder = this.documentStyle.getPropertyValue('--p-slate-100');

  constructor(
    private productService: ProductService,
    private productDispositionService: ProductDispositionService,
    // private stockTransService: StockTransactionService,
    private i18n: I18nService,
    private cdRef: ChangeDetectorRef
  ) {

    // const documentStyle = getComputedStyle(document.documentElement);

    this.options = {
      responsive: true,
      plugins: {
        legend: {
          position: 'top',
          labels: {
            color: this.textColor
          }
        },
        datalabels: {
          color: this.textColorSecondary
        }
      },
      scales: {
        x: {
          ticks: {
            color: this.textColorSecondary
          },
          grid: {
            color: this.surfaceBorder
          }
        },
        y: {
          grace: '10%',
          ticks: {
            color: this.textColorSecondary,
            padding: 10,
          },
          grid: {
            color: this.surfaceBorder
          }
        }
      }
    }

    this.fromDate = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    this.endDate = new Date();

    effect(() => {
      if (this.productIdInputSig()) {
        this.loadMinTargetStockAndDisplay();
        this.loadData();
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  dateChanged() {
    this.loadData();
  }

  async loadData() {

    if (!this.fromDate || !this.endDate)
      return;

    const loadKey = `${this.productIdInputSig()}|${this.fromDate.getTime()}|${this.endDate.getTime()}|${this.showEstimatedInboundQuantity}`;
    if (loadKey === this.lastLoadKey) {
      return;
    }
    this.lastLoadKey = loadKey;

    this.isLoadingSig.set(true);
    this.cdRef.detectChanges();

    try {
      let res = await this.productDispositionService.getProductDispositionTimeSeries({
        productIds: [this.productIdInputSig()],
        //one week ago
        fromDate: this.fromDate.toISOString() as any, //TODO fix any type
        endDate: this.endDate.toISOString() as any,
        withEstimatedInbound: true
      });

      this.productDispositionTimeSeriesEntries = res

    }
    catch (e: any) {
      console.log(e);
      this.lastLoadKey = '';
    }

    this.refreshDiagram();

    this.isLoadingSig.set(false);
    this.cdRef.detectChanges();
  }

  async loadMinTargetStockAndDisplay() {
    //can add later filter for warehouse
    let res = await this.productService.getProductGlobalMinimumAndTargetStockData(this.productIdInputSig());

    this.options.plugins.annotation = {
      annotations: {
      }
    };

    if (res.minimumStock) {
      (this.options.plugins.annotation.annotations as any).line1 = { //todo fix any
        type: 'line',
        yMin: res.minimumStock,
        yMax: res.minimumStock,
        borderColor: this.documentStyle.getPropertyValue('--p-red-500'),
        borderWidth: 2,
        label: {
          display: true,
          content: this.i18n.translate(_('warehouse.minimum-stock-x'), { x: res.minimumStock })
        }
      };
    }

    if (res.targetStock) {
      (this.options.plugins.annotation.annotations as any).line2 = { //todo fix any
        type: 'line',
        yMin: res.targetStock,
        yMax: res.targetStock,
        borderColor: this.documentStyle.getPropertyValue('--p-teal-500'),
        borderWidth: 2,
        label: {
          display: true,
          content: this.i18n.translate(_('warehouse.target-stock-x'), { x: res.targetStock })
        }
      };
    }

    this.cdRef.detectChanges();
  }

  async refreshDiagram() {

    const documentStyle = getComputedStyle(document.documentElement);


    let dataSets = [];
    let labels = []; // = this.productDispositionTimeSeriesEntries.map(x => new Date(x.date)?.toLocaleDateString());

    let stockData = [];
    let estimatedInboundQuantity = [];

    for (let i = 0; i < this.productDispositionTimeSeriesEntries.length; i++) {
      let entry = this.productDispositionTimeSeriesEntries[i];
      let label = new Date(entry.date)?.toLocaleDateString();
      labels.push(label);

      stockData.push(entry.productDisposition.totalStockQuantity);
      estimatedInboundQuantity.push(entry.estimatedInboundQuantity);
    }

    let hexColorForBackground = documentStyle.getPropertyValue('--p-primary-100');
    // make 20 %transparent
    hexColorForBackground = hexColorForBackground + '33';

    dataSets.push({
      label: this.i18n.translate(_('document-line-item.quantity-in-storage')),
      data: stockData,
      fill: true,
      tension: 0.1,
      borderColor: documentStyle.getPropertyValue('--p-primary-500'),
      backgroundColor: hexColorForBackground
    });

    if (this.showEstimatedInboundQuantity) {

      let hexColorForBackgroundEstimated = documentStyle.getPropertyValue('--p-orange-100');
      // make 90 %transparent
      hexColorForBackgroundEstimated = hexColorForBackgroundEstimated;

      dataSets.push({
        type: 'bar',
        label: this.i18n.translate(_('warehouse.estimated-inbound-quantity')),
        data: estimatedInboundQuantity,
        fill: true,
        tension: 0.1,
        borderColor: documentStyle.getPropertyValue('--p-orange-500'),
        backgroundColor: hexColorForBackgroundEstimated
      });
    }

    this.data = {
      labels: labels,
      datasets: dataSets
    };

  }

  refreshDeliveryOfGoods() {
    this.loadData();
  }

}
